import { Button, Classes, Icon, InputGroup } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import classNames from 'classnames'
import _ from 'lodash'
import React, { useEffect, useState } from 'react'

import { FramesManager } from 'shared-libs/components/view/frames-manager'
import { Entity } from 'shared-libs/models/entity'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { FormGroup } from 'browser/components/atomic-elements/atoms/form-group/form-group'
import { FormTable } from 'browser/components/atomic-elements/atoms/form-table/form-table'
import { Label } from 'browser/components/atomic-elements/atoms/label/label'
import 'browser/components/json-elements/atoms/_dictionary.scss'

const KEY_COL_WIDTH = 200
const REMOVE_COL_WIDTH = 30

/** Plain-string dictionary-key editor: commits the rename on blur (not per keystroke, which would rewrite the whole map), reverts if rejected. */
const KeyInput: React.FC<{
  value: string
  isDisabled?: boolean
  onCommit: (value: string) => boolean
}> = ({ value, isDisabled, onCommit }) => {
  const [text, setText] = useState(value)
  useEffect(() => setText(value), [value])
  return (
    <InputGroup
      fill
      value={text}
      disabled={isDisabled}
      onChange={(e) => setText(e.currentTarget.value)}
      onBlur={() => {
        if (!onCommit(text.trim())) {
          setText(value)
        }
      }}
    />
  )
}

/**
 * @uiComponent
 *
 * Editor for an object map (`{ [key]: value }`) — the map analog of `ui:list`. A lightweight,
 * schema-agnostic container: with no `entryTemplate` it renders a plain `string → key` / `string → value`
 * editor (add seeds a `newKey` placeholder); it does not inspect the schema (no enum-driven key
 * `Select`). For anything richer, supply an `entryTemplate` and drive the layout/controls yourself.
 * Add / remove / rename rewrite the whole map so numeric-string keys stay strings, not array indices.
 *
 * `entryTemplate` (not `items`, as `ui:list` uses) is a single uiSchema node describing how ONE entry
 * renders — the map's schema keywords are `additionalProperties` (value) and `propertyNames` (key), not
 * the array keyword `items`. It must keep a `*Template` suffix so the renderer leaves it raw for
 * per-entry rendering (see `abstract-renderer.tsx` materializeUiSchemaProps skip-list).
 *
 * Custom mode uses two symmetric container components inside the `entryTemplate` layout:
 * `ui:dictionaryKey` and `ui:dictionaryValue`, each wrapping its own input via `itemTemplate`. The key
 * template binds to the map key (rename on blur); the value template binds to `map[key]`. Both use
 * `value:"."`.
 *
 * These containers impose **no** styling — the `entryTemplate` layout has full control over how each
 * entry renders (side-by-side `[key : value]`, stacked like a `ui:list`, whatever). The built-in
 * stylesheet (`_dictionary.scss`: `.c-dictionaryEntry` / `.c-dictionaryKey` / `.c-dictionaryValue`) styles
 * only the *default* entry (no `entryTemplate`); a custom layout may opt into those classes via
 * `className`, or roll its own.
 *
 * Example — a fully custom string→string entry:
 * {
 *   "type": "ui:dictionary",
 *   "value": "core_integrations_ingestion_multiEntity_payloadConfig.entities",
 *   "keyLabel": "Entity alias",
 *   "valueLabel": "Entity Spec",
 *   "entryTemplate": {
 *     "type": "ui:formTable",
 *     "className": "c-dictionaryEntry",
 *     "children": [
 *       {
 *         "type": "ui:dictionaryKey",
 *         "style": { "width": 200 },
 *         "itemTemplate": { "type": "ui:input", "value": ".", "placeholder": "Enter key" }
 *       },
 *       {
 *         "type": "ui:dictionaryValue",
 *         "style": { "width": 400 },
 *         "itemTemplate": { "type": "ui:input", "value": ".", "placeholder": "Enter value" }
 *       }
 *     ]
 *   }
 * }
 *
 * TODO: the ingestion config models these connections as a map keyed by environment;
 * an array would render more naturally here, but reshaping the data model isn't worth
 * it right now.
 */
export interface IUIDictionaryProps extends IBaseProps {
  value?: Record<string, any>
  onChange: (value: any) => void
  entity: Entity
  frames: FramesManager
  isDisabled?: boolean
  errors?: any[]
  /** Column header for the key. Used only when no custom `header` is supplied (default two-column layout). */
  keyLabel?: string
  /** Column header for the value. Used only when no custom `header` is supplied (default two-column layout). */
  valueLabel?: string
  /**
   * Fully custom header: any uiSchema element (e.g. a `ui:view`/`ui:formTable` of labels), rendered
   * through the renderer above the rows. Mirror the entry's columns (reuse `c-dictionaryKey` /
   * `c-dictionaryValue` with the same widths) so the header aligns with the content. Read from the
   * uiSchema, so it isn't a real React prop; declared here for documentation. Takes precedence over
   * `keyLabel` / `valueLabel`.
   */
  header?: any
  /** Default mode: sub-path within each value to edit as text (e.g. "entityId"); omit for scalar values where `map[key]` is the text. */
  valuePath?: string
  /** Placeholder for the value input (default mode only). */
  valuePlaceholder?: string
  /** Text on the add button (default "Add Item"). */
  addButtonText?: string
  /** Seed value for a newly-added entry (via a `mapActionsToProps` expression); overrides the schema default. */
  createNewItem?: () => any
}

export class UIDictionary extends React.Component<IUIDictionaryProps> {
  public static defaultProps: Partial<IUIDictionaryProps> = {
    addButtonText: 'Add Item',
  }

  public render() {
    const { addButtonText } = this.props
    const entries = this.getEntries()
    const hasCustomEntry = !_.isNil(this.getEntryTemplateUiSchema())
    return (
      <div className="c-dictionary">
        {!_.isEmpty(entries) && this.renderHeader()}
        {entries.map((key) => this.renderRow(key, hasCustomEntry))}
        <Button
          className={classNames('c-abstractList-addButton', Classes.MINIMAL)}
          icon="add"
          small
          disabled={this.isDisabled()}
          onClick={this.handleAddItem}
        >
          {addButtonText}
        </Button>
      </div>
    )
  }

  /**
   * Table header. A custom `entryTemplate` layout should supply its own `header` uiSchema element
   * (rendered through the renderer) so it can mirror the entry's column widths exactly — reuse the same
   * `c-dictionaryKey` / `c-dictionaryValue` cells and it aligns by construction. Falls back to the
   * `keyLabel` / `valueLabel` two-column layout for the default (no-`entryTemplate`) dictionary.
   */
  private renderHeader() {
    const headerUiSchema = this.getHeaderUiSchema()
    if (!_.isNil(headerUiSchema)) {
      return this.renderCustomHeader(headerUiSchema)
    }
    const { keyLabel, valueLabel } = this.props
    if (!keyLabel || !valueLabel) {
      return null
    }
    return (
      <div
        className="u-flex u-alignItemsCenter c-placeholderColor u-bumperBottom--xs"
        style={{ gap: 8 }}
      >
        <div style={{ width: KEY_COL_WIDTH }}>{keyLabel}</div>
        <div style={{ flex: 1 }}>{valueLabel}</div>
        <div style={{ width: REMOVE_COL_WIDTH }} />
      </div>
    )
  }

  private getHeaderUiSchema(): any {
    return this.props.frames.getContext('uiSchema').header
  }

  /** Render the schema-supplied `header` element through the renderer (static, no entry binding). */
  private renderCustomHeader(headerUiSchema: any) {
    const { frames } = this.props
    const renderer = frames.getContext('renderer')
    if (!renderer) {
      return null
    }
    const uiSchemaPath = frames.getContext('uiSchemaPath').concat('header')
    const newFrames = renderer.createChildFrame(frames, { uiSchema: headerUiSchema, uiSchemaPath })
    return renderer.createElementFromFrame(newFrames, {})
  }

  private renderRow(key: string, hasCustomEntry: boolean) {
    return (
      <div key={key} className="c-dictionaryRow u-bumperBottom--sm">
        <div className="c-dictionaryRow-content">
          {hasCustomEntry ? this.renderCustomEntry(key) : this.renderDefaultEntry(key)}
        </div>
        {/* A disabled dictionary is read-only (e.g. a Review summary) — no remove control. */}
        {!this.isDisabled() && (
          <Button
            className={classNames('c-abstractListItem-removeButton', Classes.MINIMAL)}
            onClick={() => this.handleRemoveItem(key)}
          >
            <Icon icon={IconNames.CROSS} />
          </Button>
        )}
      </div>
    )
  }

  private renderDefaultEntry(key: string) {
    const { valuePath, valuePlaceholder } = this.props
    const isDisabled = this.isDisabled()
    // An object value with no valuePath can't be text-edited; disable rather than clobber it.
    const isUneditableObject = !valuePath && _.isObject(_.get(this.props.value, [key]))
    return (
      <FormTable className="c-dictionaryEntry">
        <div className="c-dictionaryKey" style={{ width: KEY_COL_WIDTH }}>
          {this.renderKey(key)}
        </div>
        <div className="c-dictionaryValue">
          <InputGroup
            fill
            placeholder={
              isUneditableObject
                ? 'Object value — set "valuePath" to edit a field'
                : valuePlaceholder
            }
            value={this.getDefaultValueText(key)}
            disabled={isDisabled || isUneditableObject}
            onChange={(e) => this.handleDefaultValueChange(key, e.currentTarget.value)}
          />
        </div>
      </FormTable>
    )
  }

  private getDefaultValueText(key: string): string {
    const { valuePath } = this.props
    const entry = _.get(this.props.value, [key])
    const raw = valuePath ? _.get(entry, valuePath) : entry
    if (_.isObject(raw)) {
      return '' // never '[object Object]'; see the isUneditableObject guard in renderDefaultEntry
    }
    return _.isNil(raw) ? '' : String(raw)
  }

  private renderCustomEntry(key: string) {
    return createDictionaryEntryElement({
      frames: this.props.frames,
      entryKey: key,
      entryValue: _.get(this.props.value, [key]),
      isDisabled: this.isDisabled(),
      onRenameKey: (newKey: string) => this.handleKeyChange(key, newKey),
    })
  }

  private renderKey(key: string) {
    return (
      <KeyInput
        value={key}
        isDisabled={this.isDisabled()}
        onCommit={(newKey: string) => this.handleKeyChange(key, newKey)}
      />
    )
  }

  private isDisabled(): boolean {
    return Boolean(this.props.isDisabled)
  }

  private getEntries(): string[] {
    return _.keys(this.props.value || {})
  }

  private getEntryTemplateUiSchema(): any {
    return this.props.frames.getContext('uiSchema').entryTemplate
  }

  private handleAddItem = () => {
    const { createNewItem } = this.props
    const used = new Set(this.getEntries())
    // For an enum-keyed map (propertyNames is a closed enum, e.g. environmentTag), seed the next
    // unused enum member so the added key is valid; otherwise fall back to a `newKey` placeholder.
    const newKey = this.nextEnumKey(used) ?? this.nextPlaceholderKey(used)
    const newValue = typeof createNewItem === 'function' ? createNewItem() : this.defaultEntryValue()
    this.commit({ ...this.props.value, [newKey]: newValue })
  }

  /** First unused member of the key enum (`propertyNames.enum`), or undefined when the map isn't enum-keyed or every member is already used. */
  private nextEnumKey(used: Set<string>): string | undefined {
    const { frames } = this.props
    const renderer = frames.getContext('renderer')
    const dataSchemaPath = frames.getContext('dataSchemaPath')
    const keySchema =
      !_.isEmpty(dataSchemaPath) && renderer
        ? renderer.getComponentDataSchema(dataSchemaPath.concat('propertyNames'))
        : undefined
    const enumValues: string[] = keySchema?.enum || []
    return enumValues.find((v) => !used.has(v))
  }

  /** A `newKey`, `newKey2`, … placeholder not already in `used`, for adding a string key. */
  private nextPlaceholderKey(used: Set<string>): string {
    let candidate = 'newKey'
    let i = 2
    while (used.has(candidate)) {
      candidate = `newKey${i++}`
    }
    return candidate
  }

  private handleRemoveItem(key: string) {
    this.commit(_.omit(this.props.value || {}, [key]))
  }

  private handleDefaultValueChange(key: string, text: string) {
    const { valuePath } = this.props
    const value = this.props.value || {}
    // Whole-map write (numeric-key safe); valuePath sets that sub-field on a cloned entry, forcing objects so numeric sub-keys stay keys.
    const entryValue = valuePath
      ? _.setWith(_.cloneDeep(value[key]) || {}, valuePath, text, Object)
      : text
    this.commit({ ...value, [key]: entryValue })
  }

  /**
   * Rename a key (whole-map rewrite). Returns false → the key control reverts. Rejected when the new key
   * is empty, unchanged, or already present — a collision reverts rather than silently dropping the
   * entry that holds it. (`_.has([newKey])` uses an array path so a dot-containing key is matched
   * literally, not as a nested path.)
   */
  private handleKeyChange(oldKey: string, newKey: string): boolean {
    if (!newKey || newKey === oldKey || _.has(this.props.value, [newKey])) {
      return false
    }
    const next: Record<string, any> = {}
    _.forEach(this.props.value || {}, (v, k) => {
      next[k === oldKey ? newKey : k] = v
    })
    this.commit(next)
    return true
  }

  private commit(value: Record<string, any>) {
    this.props.onChange(value)
  }

  /** Value for a newly-added entry: the schema `default`, else `undefined` (an empty entry, dropped on save if untouched). */
  private defaultEntryValue(): any {
    const { frames } = this.props
    const renderer = frames.getContext('renderer')
    const dataSchemaPath = frames.getContext('dataSchemaPath')
    const valueSchema =
      !_.isEmpty(dataSchemaPath) && renderer
        ? renderer.getComponentDataSchema(dataSchemaPath.concat('additionalProperties'))
        : undefined
    return _.has(valueSchema, 'default') ? _.cloneDeep(valueSchema.default) : undefined
  }
}

/**
 * @uiComponent
 *
 * Key control for a `ui:dictionary` custom entry. Reads the key + rename handler from frame context,
 * so it works anywhere in the `entryTemplate` layout.
 *
 * Two modes:
 * - **Custom** — with an `itemTemplate` (any scalar control, e.g. `ui:input` / `ui:select`, with no
 *   `value` binding). We seed the template via `defaultProps`: its value is the draft key and its
 *   onChange edits that draft. A closed-enum key (a `ui:select` over `propertyNames.enum`) commits the
 *   rename immediately; a free-text key commits on blur (not per keystroke, which would re-key the whole
 *   map mid-edit and steal focus). The `itemTemplate` layout should match `ui:dictionaryValue`'s for
 *   columns to align.
 * - **Built-in** (no `itemTemplate`) — a plain-string key input. An explicit `label` lays it out
 *   horizontally like `ui:inputField`; omit it when a column header names the key.
 */
export const UIDictionaryKey: React.FC<any> = ({
  frames,
  className,
  style,
  label,
  itemTemplate,
}) => {
  const entryKey = frames.getContext('dictionaryEntryKey')
  const onRename = frames.getContext('renameDictionaryKey')
  const isDisabled = frames.getContext('dictionaryEntryDisabled')

  // Draft the pending key locally so a free-text edit doesn't rewrite the whole map (and remount this
  // row) on every keystroke; the custom-mode branch decides when to commit. Resync when the committed
  // key changes underneath us.
  const [draftKey, setDraftKey] = useState(entryKey)
  useEffect(() => setDraftKey(entryKey), [entryKey])

  if (!_.isNil(itemTemplate)) {
    // Is the key a closed enum (e.g. a `ui:select` over `propertyNames.enum`)? A closed-set control
    // emits a complete, valid key on every change, so we commit the rename immediately. A free-text
    // key must instead debounce to blur — committing per keystroke would re-key the whole map and
    // remount this row, stealing focus mid-typing.
    const renderer = frames.getContext('renderer')
    const keyDataSchemaPath = frames.getContext('dictionaryKeyDataSchemaPath')
    const keySchema = !_.isEmpty(keyDataSchemaPath) && renderer?.getComponentDataSchema(keyDataSchemaPath)
    const isEnumKey = !_.isEmpty(keySchema?.enum)

    // Commit `candidate` as the new key; revert the draft if it's empty or the rename is rejected.
    const commit = (candidate: string) => {
      const next = (candidate || '').trim()
      if (!next || !onRename || !onRename(next)) {
        setDraftKey(entryKey)
      }
    }
    const handleChange = (newKey: string) => {
      setDraftKey(newKey)
      // A select commits its choice on menu-close, which races the wrapper's onBlur (whose closure
      // would still hold the stale draft). Commit here with the fresh value instead of on blur.
      if (isEnumKey) {
        commit(newKey)
      }
    }
    return (
      // No imposed styling — the caller's layout has full control. For a free-text key, onBlur
      // (focusout) commits the rename; an enum key has already committed in handleChange.
      <div
        className={className}
        style={style}
        onBlur={isEnumKey ? undefined : () => commit(draftKey)}
      >
        {createDictionaryChildElement({
          frames,
          // Strip any `value` binding: a key isn't backed by an entity path, so it must NOT be
          // path-bound. If it were (e.g. a stray `value:"."` in the schema, or one inherited via a
          // deep-merged debug/deployed uiSchema that _.merge can't delete), the renderer would build a
          // path-bound onChange that overrides ours below, and selecting a key would clobber the entry
          // value. With no `value`, the renderer builds no formProps and our defaultProps win.
          template: _.omit(itemTemplate, 'value'),
          isDisabled,
          // Key schema is `propertyNames` (so a `ui:select` resolves its enum). We hand the control the
          // draft key + rename via `defaultProps`; it just needs to produce a scalar key.
          dataSchemaPath: keyDataSchemaPath,
          defaultProps: {
            value: draftKey,
            onChange: handleChange,
          },
        })}
      </div>
    )
  }

  const control = (
    <KeyInput
      value={entryKey}
      isDisabled={isDisabled}
      onCommit={(newKey) => (onRename ? onRename(newKey) : false)}
    />
  )
  if (!label) {
    return (
      <div className={className} style={style}>
        {control}
      </div>
    )
  }
  return (
    <FormGroup className={className} isHorizontalLayout>
      <Label isHorizontalLayout text={label} />
      <div className="c-formGroup-horizontalContent">{control}</div>
    </FormGroup>
  )
}

/**
 * @uiComponent
 *
 * Value control for a `ui:dictionary` custom entry — the symmetric partner of `ui:dictionaryKey`.
 * Renders its `itemTemplate` bound to the entry value (`map[key]`, a real entity path), so children
 * bind normally: `value:"."` for the whole value, `value:"someField"` for a sub-field. Children given
 * inline (`children`) instead of `itemTemplate` also work, since the entry frame's `valuePath` is
 * already `map[key]`; `itemTemplate` is preferred for symmetry with the key column.
 */
export const UIDictionaryValue: React.FC<any> = ({
  frames,
  className,
  style,
  itemTemplate,
  children,
}) => {
  return (
    // No imposed styling; binds `itemTemplate` (or inline `children`) to the entry value at `map[key]`.
    <div className={className} style={style}>
      {_.isNil(itemTemplate)
        ? children
        : createDictionaryChildElement({ frames, template: itemTemplate })}
    </div>
  )
}

/**
 * Renders a child `template` for `ui:dictionaryKey` / `ui:dictionaryValue` through the renderer.
 * `defaultProps` seed the child component (the key column passes its draft key + rename `onChange` this
 * way, since the key isn't backed by an entity path). `dataSchemaPath` reparents schema resolution (the
 * key points it at `propertyNames` so an enum control resolves its options).
 */
export function createDictionaryChildElement(props): React.ReactElement<any> | null {
  const { frames, template, defaultProps, isDisabled, dataSchemaPath } = props
  const renderer = frames.getContext('renderer')
  if (!template || !renderer) {
    return null
  }
  const uiSchemaPath = frames.getContext('uiSchemaPath').concat('itemTemplate')
  const context: any = {
    uiSchema: template,
    uiSchemaPath,
    dictionaryEntryDisabled: isDisabled,
  }
  // Only override the schema base when non-empty; an empty path would let the child inherit the
  // entry's value schema, which createChildFrame handles fine via `value` resolution.
  if (!_.isEmpty(dataSchemaPath)) {
    context.dataSchemaPath = dataSchemaPath
    // Resolve the dataSchema here too. The renderer only derives `dataSchema` from a path when the node
    // has a `uiSchema.value` binding; the key control has none (so its onChange stays ours, not a
    // path write), so we must supply the resolved schema — otherwise a `ui:select` key gets no enum
    // options and can't be changed.
    context.dataSchema = renderer.getComponentDataSchema(dataSchemaPath)
  }
  const newFrames = renderer.createChildFrame(frames, context)
  return renderer.createElementFromFrame(newFrames, defaultProps || {})
}

/**
 * Renders one custom entry through the renderer (the map analog of `ui:list`'s createListItemElement):
 * resolves the value subschema at `additionalProperties`, binds the value path at the entry key, and
 * injects the key / rename handler / key-schema path into frame context for `ui:dictionaryKey`.
 */
function createDictionaryEntryElement(props: {
  frames: FramesManager
  entryKey: string
  entryValue: any
  isDisabled: boolean
  onRenameKey: (newKey: string) => boolean
}) {
  const { frames, entryKey, entryValue, isDisabled, onRenameKey } = props
  const renderer = frames.getContext('renderer')
  const uiSchema = frames.getContext('uiSchema').entryTemplate
  if (!uiSchema) {
    return null
  }
  const uiSchemaPath = frames.getContext('uiSchemaPath').concat('entryTemplate')
  const dictDataSchemaPath = frames.getContext('dataSchemaPath')
  const dictDataSchema = frames.getContext('dataSchema')
  let dataSchema = dictDataSchema
  let dataSchemaPath = dictDataSchemaPath
  let valuePath
  // Key schema = the map's `propertyNames` (its enum), routed to the key template so a `ui:select`
  // resolves options. Guarded on existence so plain string-keyed maps stay schema-less (no resolver error).
  let keyDataSchemaPath
  if (!_.isEmpty(dictDataSchemaPath)) {
    if (dictDataSchema?.propertyNames) {
      keyDataSchemaPath = dictDataSchemaPath.concat('propertyNames')
    }
    dataSchemaPath = dictDataSchemaPath.concat('additionalProperties')
    dataSchema = renderer.getComponentDataSchema(dataSchemaPath)
    // A schema-bound dictionary normally has a parent valuePath; fall back to a root-relative
    // binding if it doesn't, rather than throwing on the concat.
    valuePath = frames.getContext('valuePath')?.concat(entryKey) ?? [entryKey]
  }
  const context = {
    dataSchema,
    dataSchemaPath,
    uiSchema,
    uiSchemaPath,
    valuePath,
    // Entry extras consumed by `ui:dictionaryKey` (and available to any descendant via frames).
    dictionaryEntryKey: entryKey,
    dictionaryKeyDataSchemaPath: keyDataSchemaPath,
    dictionaryEntryDisabled: isDisabled,
    renameDictionaryKey: onRenameKey,
  }
  const state = [entryValue, { key: entryKey, value: entryValue }]
  const newFrames = renderer.createChildFrame(frames, context, state)
  return renderer.createElementFromFrame(newFrames, {})
}
